import {
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  useEventCallback,
} from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { transformerPersonToRegisterOffice } from 'shell/components/shared/sipo-person-form/transformer-person-to-register-office';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { noop } from 'shell/utils/noop';
import Table from 'shell/components/shared/table';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import {
  BodyTablePersonsVM,
  PersonalDataSearchVM,
  PersonalDataVM,
} from 'shell/view-models/personal-data';
import { HeadCell } from '../table/types';
import PmiTextField from '../text-field';
import { FormikErrors, useFormik } from 'formik';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import ChooseDate from '../choice-date';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { DateTime } from 'luxon';
import { StickyDiv } from '../sticky-div';
import { PmiButton } from '../button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NewRegistrationPersonModal from 'shell/components/shared/registration-person-sipo-modal';
import { regexNumericFiscalCode } from 'shell/constants/regex';
import { defaultChoicePickerState } from '../choice-date/constants';

export const headCells: HeadCell<BodyTablePersonsVM, keyof BodyTablePersonsVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'birthday',
    label: 'Data di nascita',
  },
  {
    id: 'birthplace',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'state',
    label: 'Stato',
  },
  {
    id: 'action',
    label: '',
  },
];

export const mapPersonToRow = (person: PersonalDataVM): BodyTablePersonsVM => {
  let birthplace = '';
  const personAnpr = person.subjectData || {};
  const residenceArray = personAnpr.residenza || [];
  // birthplace check for object
  if (personAnpr.generalita?.luogoNascita?.localita) {
    birthplace = `${personAnpr.generalita?.luogoNascita?.localita.descrizioneLocalita} (${personAnpr.generalita?.luogoNascita?.localita.descrizioneStato})`;
  }

  if (personAnpr.generalita?.luogoNascita?.comune) {
    birthplace = `${personAnpr.generalita?.luogoNascita?.comune.nomeComune} (${personAnpr.generalita?.luogoNascita?.comune.siglaProvinciaIstat})`;
  }
  // Residence check for object
  let residence = '';
  // scelgo il primo o è un array?
  if (residenceArray[0]?.indirizzo) {
    residence = `${residenceArray[0]?.indirizzo.comune?.nomeComune || ''} (${
      residenceArray[0]?.indirizzo.comune?.siglaProvinciaIstat || ''
    })`;
  }
  // scelgo il primo o è un array?
  if (residenceArray[0]?.localitaEstera) {
    residence = `${residenceArray[0]?.localitaEstera?.indirizzoEstero?.localita?.descrizioneLocalita} (${residenceArray[0]?.localitaEstera?.indirizzoEstero?.localita?.descrizioneStato})`;
  }

  let state = '';
  // state check for object
  if (residenceArray[0]?.indirizzo?.toponimo) {
    const toponym = residenceArray[0]?.indirizzo?.toponimo;

    const civic = residenceArray[0]?.indirizzo?.numeroCivico;

    state = `${toponym.specie} ${toponym.denominazioneToponimo},${civic?.numero}${
      civic?.esponente1 ? `/${civic.esponente1}${civic.lettera}` : ''
    }`;
  }

  if (personAnpr.generalita?.soggettoAIRE && personAnpr.generalita?.soggettoAIRE === 'S') {
    state = 'Iscritto AIRE';
  }

  if (
    personAnpr.datiCancellazione &&
    personAnpr.datiCancellazione?.descrizioneMotivoCancellazione
  ) {
    state = `${personAnpr.datiCancellazione.descrizioneMotivoCancellazione}`;
  }
  return {
    name: person.generality?.name,
    surname: person.generality?.surname,
    fiscalCode: person.generality?.fiscalCode,
    birthday: person.generality?.birthDate?.toFormat('dd/MM/yyyy'),
    gender: person.generality?.gender,
    birthplace,
    state,
    residence,
    item: person,
    hidden: {
      edit: true,
      delete: true,
      read: false,
    },
  };
};

export interface FormSearchPersonProps {
  disabled?: boolean;
  url?: string;
  onSuccess: (value: PersonalDataVM) => void;
  goBackUrl?: string;
  onSipoPersonSelected: (value?: PersonalDataVM) => void;
  initialValues?: PersonalDataSearchVM;
}

const FormSearchPersonComponent = ({
  disabled,
  url = `${__PERSON_NEW_API_URL__}/persona`,
  onSuccess,
  goBackUrl,
  onSipoPersonSelected,
  initialValues,
}: FormSearchPersonProps): React.ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const fiscalCode = queryParams.get('codiceFiscale') || '';

  const [openModalNewPerson, setOpenModalNewPerson] = useState<{
    open: boolean;
    id?: string;
    idAnpr?: string;
    data?: PersonalDataVM;
    isFromTable?: boolean;
  }>({ open: false });

  // ricerca sia su ANPR che DB
  const getPerson = useHttpClient<HttpResponse<ResponseAnprData>, PersonalDataVM>(url, {
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => {
      const data = transformerPersonToRegisterOffice(response);

      return {
        ...data.accountholderData,
        fiscalCode: data.accountholderData?.generality?.fiscalCode,
        warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
          (warning) => ({
            id: warning.codiceErroreAnomalia,
            value: warning.testoErroreAnomalia,
          })
        ),
        errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
          id: error.codiceErroreAnomalia,
          value: error.testoErroreAnomalia,
        })),
      };
    },
  });

  const onSearch = useEventCallback((values: PersonalDataSearchVM) => {
    setVisibility({});

    const hasSearchBirthFilters = !!values?.birthdayChoice && !!values?.birthday;

    const data = {
      datiRichiesta: {
        ricercaNazionale: '1', // fixed
      },
      datiNascita: hasSearchBirthFilters
        ? {
            senzaGiorno:
              !values?.birthdayChoice?.includes('day') && values?.birthdayChoice?.includes('month')
                ? '1'
                : undefined,
            senzaGiornoMese:
              !values?.birthdayChoice?.includes('day') && !values?.birthdayChoice?.includes('month')
                ? '1'
                : undefined,
            dataEvento: values?.birthday?.toFormat('yyyy-MM-dd') || undefined,
          }
        : undefined,
      criteriRicerca: {
        codiceFiscale: values?.fiscalCode || undefined,
        cognome: values?.surname || undefined,
        nome: values?.name || undefined,
        senzaCognome: values?.withoutSurname ? '1' : undefined,
        senzaNome: values?.withoutName ? '1' : undefined,
      },
    };

    (getPerson({ data }) as ResponsePromise<HttpResponse<ResponseAnprData>, PersonalDataVM>).then(
      (response) => {
        const castedResponse = response as PersonalDataVM;

        if (castedResponse?.openModalNewPerson && setOpenModalNewPerson) {
          // open modal registrazione nuova persona
          setOpenModalNewPerson({
            open: true,
            data: {
              fiscalCode: values?.fiscalCode,
              name: values?.name,
              surname: values?.surname,
              birthday: values?.birthday,
            },
          });
          return;
        }

        if (castedResponse?.residenceOrigin?.irreperibile && setOpenModalNewPerson) {
          // open modal registrazione nuova persona anche se è irreperibile
          setOpenModalNewPerson({
            open: true,
            id: castedResponse?.idSchedaSoggetto,
            idAnpr: castedResponse?.generality?.anprIdentifier,
            data: castedResponse,
          });
          onChangeFields({
            ...(castedResponse || {}),
            table: {
              page: 0,
              rowsPerPage: 15,
              body: [
                mapPersonToRow({
                  ...castedResponse,
                }),
              ],
            }, // reset table
            withSearch: true, // la persona è il risultato della ricerca
          });
          return;
        } else if (castedResponse?.isSipo && setOpenModalNewPerson) {
          // open modal registrazione nuova persona
          setOpenModalNewPerson({
            open: true,
            id: castedResponse?.idSchedaSoggetto,
            data: castedResponse,
          });
          onChangeFields({
            ...(castedResponse || {}),
            table: {
              page: 0,
              rowsPerPage: 15,
              body: [
                mapPersonToRow({
                  ...castedResponse,
                }),
              ],
            },
          });
          return;
        }

        onChangeFields(castedResponse);

        if (castedResponse.subjectData) {
          onSuccess(castedResponse);
        }
      }
    );
  });

  const filterInitialValues: PersonalDataSearchVM | undefined = useMemo(() => {
    if (!initialValues || Object.values(initialValues || {}).every((el) => !el)) {
      return;
    }
    return {
      ...(initialValues || {}),
      birthdayChoice: defaultChoicePickerState,
      fiscalCode: initialValues?.fiscalCode,
    };
  }, [initialValues]);

  const filters = useFormik<PersonalDataSearchVM>({
    initialValues: {
      ...(initialValues || {}),
      birthdayChoice: defaultChoicePickerState,
      fiscalCode: fiscalCode || initialValues?.fiscalCode,
    } as PersonalDataSearchVM,
    onSubmit: onSearch,
  });

  useEffect(() => {
    if (filterInitialValues) {
      onSearch(filterInitialValues);
    }
  }, []);

  const { values, errors, setFieldValue, setValues } = filters;

  const onChangeFields = useEventCallback((newValues: PersonalDataSearchVM) =>
    setValues({ ...values, ...newValues })
  );

  const onChangePage = useEventCallback((page: number) => {
    setFieldValue('table', {
      ...(values?.table || {}),
      page: page || 0,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setFieldValue('table', {
      ...(values?.table || {}),
      page: 0,
      rowsPerPage: rowsPerPage || 0,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyTablePersonsVM): Promise<BodyTablePersonsVM> => {
      return new Promise(() => {
        if (values?.table?.body?.length === 1) {
          onSuccess({
            ...cell.item,
            fiscalCode: cell.item?.generality?.fiscalCode,
          });
          return;
        }
        if (cell?.item?.residenceOrigin?.irreperibile && setOpenModalNewPerson) {
          // open modal registrazione nuova persona anche se è irreperibile
          setOpenModalNewPerson({
            open: true,
            id: cell?.item?.generality?.municipalityIdentifier,
            idAnpr: cell?.item?.generality?.anprIdentifier,
            data: cell?.item,
            isFromTable: true,
          });
          return;
        } else if (cell?.item?.isSipo && setOpenModalNewPerson) {
          // open modal registrazione nuova persona
          setOpenModalNewPerson({
            open: true,
            id: cell?.item?.generality?.municipalityIdentifier,
            data: cell?.item,
            isFromTable: true,
          });
          return;
        }
        if (cell.item?.generality?.fiscalCode) {
          onSuccess({
            ...cell.item,
            fiscalCode: cell.item?.generality?.fiscalCode,
          });
        }
      });
    }
  );

  const handleBirthDate = useEventCallback((value?: DateTime | null, choice?: DatePickerView[]) => {
    onChangeFields({
      ...(values || {}),
      birthday: value,
      birthdayChoice: choice,
    });
  });

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (values?.errors && values?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        values?.errors?.length > 1 ? `Ci sono ${values?.errors?.length} errori` : `C'è 1 errore`;
      errorToVisualize.messages = values?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, values?.errors]);

  const fiscalCodeMaxLength = useMemo(() => {
    if (values?.fiscalCode) {
      if (regexNumericFiscalCode.test(values?.fiscalCode) && values?.fiscalCode?.length >= 10) {
        return '11';
      }
      return '16';
    }
  }, [values?.fiscalCode]);

  return (
    <form onSubmit={filters.handleSubmit} onReset={filters.handleReset}>
      <Grid container spacing={2}>
        {!errorMessage.title || !values?.warnings ? undefined : (
          <Grid item xs={12}>
            <SearchMessages
              type={MessageType.Error}
              title={errorMessage.title || ''}
              messages={errorMessage.messages || []}
              visibility={errorMessage.visibility || false}
              onChangeVisibility={onVisibility}
            />
            <SearchMessages
              type={MessageType.Warning}
              title={`Ci sono ${values?.warnings?.length || ''} warning`}
              messages={values?.warnings || []}
              visibility={
                visibility.warning !== undefined
                  ? visibility.warning
                  : (values?.warnings || [])?.length > 0
              }
              onChangeVisibility={onVisibility}
            />
          </Grid>
        )}
        <Grid item xs={12} md={3}>
          <StickyDiv>
            <div className="pmi-pb-4 pmi-d-block">
              <PmiButton variant="contained" color="cdmBrandThird" type="submit" fullWidth>
                Cerca
              </PmiButton>
            </div>
            <div className="pmi-pb-4 pmi-d-block">
              <PmiButton variant="outlined" color="cdmBrandThird" type="reset" fullWidth>
                Pulisci
              </PmiButton>
            </div>
            <div className="pmi-d-block">
              <PmiButton
                variant="outlined"
                color="cdmBrand"
                fullWidth
                onClick={() => navigate(goBackUrl || '')}
              >
                Indietro
              </PmiButton>
            </div>
          </StickyDiv>
        </Grid>

        <Grid item xs={12} md={9}>
          <Card raised>
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                Filtri
              </Typography>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <PmiTextField
                      label="Codice fiscale"
                      aria-label="Codice fiscale"
                      id="fiscal-code"
                      fullWidth
                      value={values?.fiscalCode?.toUpperCase() || ''}
                      onChange={(event) => setFieldValue('fiscalCode', event.target.value)}
                      disabled={disabled}
                      inputProps={{ maxLength: fiscalCodeMaxLength ? fiscalCodeMaxLength : '16' }}
                      error={
                        !!(errors?.generality as FormikErrors<AccordionGeneralityForm>)?.fiscalCode
                      }
                      helperText={
                        (errors?.generality as FormikErrors<AccordionGeneralityForm>)?.fiscalCode
                      }
                    />
                  </Grid>
                  <Grid item container spacing={1} xs={12} alignItems="center">
                    <Grid item xs={12} md={9}>
                      <PmiTextField
                        label="Cognome"
                        aria-label="Cognome"
                        id="surname"
                        fullWidth
                        value={values?.surname || ''}
                        onChange={(event) => setFieldValue('surname', event.target.value)}
                        disabled={disabled || values?.withoutSurname}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="withoutSurname"
                            checked={values?.withoutSurname}
                            onChange={() =>
                              onChangeFields({
                                surname: !values?.withoutSurname ? '' : values?.surname || '',
                                withoutSurname: !values?.withoutSurname,
                              })
                            }
                            disabled={disabled}
                            inputProps={{ 'aria-label': 'Persona senza cognome' }}
                          />
                        }
                        label="Persona senza cognome"
                      />
                    </Grid>
                  </Grid>
                  <Grid item container spacing={1} xs={12} alignItems="center">
                    <Grid item xs={12} md={9}>
                      <PmiTextField
                        label="Nome"
                        aria-label="Nome"
                        id="name"
                        fullWidth
                        value={values?.name || ''}
                        onChange={(event) => setFieldValue('name', event.target.value)}
                        disabled={disabled || values?.withoutName}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="withoutName"
                            checked={values?.withoutName}
                            onChange={() =>
                              onChangeFields({
                                name: !values?.withoutName ? '' : values?.name || '',
                                withoutName: !values?.withoutName,
                              })
                            }
                            disabled={disabled}
                            inputProps={{ 'aria-label': 'Persona senza nome' }}
                          />
                        }
                        label="Persona senza nome"
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <ChooseDate
                      selected={values?.birthday}
                      choice={values?.birthdayChoice}
                      onChange={handleBirthDate}
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <NewRegistrationPersonModal
          open={openModalNewPerson.open}
          onClose={() => setOpenModalNewPerson({ open: false })}
          onNo={() => setOpenModalNewPerson({ open: false })}
          onYes={() => {
            onSipoPersonSelected(openModalNewPerson.data);
            setOpenModalNewPerson({ open: false });
          }}
          onProceed={() => {
            onSuccess(openModalNewPerson.data || {});
          }}
          id={openModalNewPerson.id}
          idAnpr={openModalNewPerson.idAnpr}
          isFromTable={openModalNewPerson.isFromTable}
        />
        {values?.table?.body ? (
          <Grid item xs={12}>
            <Table
              ariaLabel="Lista Persone"
              page={values?.table?.page || 0}
              rowsPerPage={values?.table?.rowsPerPage}
              body={(values?.table?.body || []) as Array<BodyTablePersonsVM>}
              count={values?.table?.body?.length || 0}
              head={headCells}
              onSort={noop}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              orders={{}}
              isNotLayoutFixed
              noSort
              onVisibilityPress={onVisibilityPress}
              //hideButtons
            />
          </Grid>
        ) : undefined}
      </Grid>
    </form>
  );
};

FormSearchPersonComponent.displayName = 'Form search person component';

const FormSearchPerson = memo(FormSearchPersonComponent);

export default FormSearchPerson;
